import { useCallback, useState } from 'react';
import { ANALYTICS_EVENTS, type Surface } from '@shared/analytics/events';
import type { StateDimension } from '@shared/schemas/flow-contact-state';
import { useEnrollQueue } from '../EnrollQueue/EnrollQueueContext';
import { useSegmentTrack } from '../../../hooks/useSegmentTrack';
import { useCampaignMemberSidebarStore } from './campaign-member-sidebar-store';

/**
 * Intent-action hooks for the contact-state fact cards (plan 005 U1c lift) —
 * the click handlers `StatePills.tsx`'s `AddToQueueButton` / `WarmIntroAddButton`
 * / `CampaignMemberOpenButton` used to own inline, now shared with V2's
 * `EntityStateFactCard` (U13). Each hook returns ONLY the public
 * state/handlers a button needs to render — no JSX, no Glaze/Tailwind.
 *
 * `requests-open` (the `flow:request:<id>` intent, `RequestsOpenButton`) has
 * no hook here — it is already a one-line wrapper over
 * `useEnrollQueue().openRequestDetail`, so extracting it would just rename
 * the same one line.
 */

/**
 * The "Add to queue" action for a `flow:enroll-suggest:<campaignId>` fact
 * (2026-07-23 decision #3 — the suggested-tier invite is NOT a link, it's a
 * one-click enqueue-with-play). `campaignId` maps to a live PlayOption via
 * `play_instance_ref` (the queue's own play catalog, exposed by
 * `EnrollQueueContext`); with no match, adds unassigned instead of erroring —
 * the rep assigns the play manually.
 *
 * Already-queued idiom matches `WorkflowsDropdown`'s "✓ Queued" state: NOT
 * inert-disabled — the returned `onClick` stays callable, and a click just
 * opens the sidebar instead of re-adding (`addQueueItem` is idempotent per
 * contact server-side too, but there's no reason to re-POST when the click's
 * real intent is "show me the queue").
 *
 * Cache invalidation (U-C) is centralized in `EnrollQueueContext`'s
 * `invalidateContactState` helper (wired into `addContact`'s own mutation) —
 * this hook does not run any invalidation of its own.
 *
 * U-B (play-catalog race guard): while the play catalog fetch hasn't
 * SETTLED yet (`!playsResolved`) and this contact isn't already queued,
 * `catalogLoading` is `true` — a click at that moment would silently fall
 * back to the unassigned-add path even when a matching live play exists,
 * because `matchedPlay` can only find a match once `plays` is populated.
 * `playsResolved` unlocks on EITHER success or failure of that fetch (never
 * on the queued branch, which needs no catalog at all to know "open the
 * sidebar").
 */
export function useAddToQueueFromSuggestion(contactId: string, campaignId: string) {
  const { plays, groups, addContact, open, playsResolved } = useEnrollQueue();
  const [pending, setPending] = useState(false);

  const queued = groups.some(g => g.items.some(it => it.contact_id === contactId));
  const matchedPlay = plays.find(p => p.play_instance_ref === campaignId) ?? null;
  const catalogLoading = !queued && !playsResolved;

  const onClick = useCallback(async () => {
    if (pending || catalogLoading) return;
    if (queued) {
      open();
      return;
    }
    setPending(true);
    try {
      await addContact(contactId, {
        sourceContext: 'suggested-invite',
        ...(matchedPlay ? { playTypeId: matchedPlay.play_type_id, playInstanceRef: matchedPlay.play_instance_ref } : {}),
      });
      open();
    } finally {
      setPending(false);
    }
  }, [pending, catalogLoading, queued, open, addContact, contactId, matchedPlay]);

  const title = catalogLoading
    ? 'Loading play catalog…'
    : queued
      ? 'Already in the queue — open the queue'
      : matchedPlay
        ? `Add to queue for ${matchedPlay.name}`
        : 'Add to queue — no live play matched this campaign; assign manually';

  return { queued, pending, catalogLoading, title, onClick };
}

/**
 * The "Add to queue" action for a `flow:warm-intro:<warmIntroId>` fact
 * (warm-intro plan U6, R8 — mirrors `useAddToQueueFromSuggestion` above
 * almost exactly): a click adds the contact with this connector's
 * `salesforce_warm_intro.id` preselected (`addContact`'s `warmIntroId` opt)
 * plus the `warm_intro` play type itself, resolved from the loaded catalog
 * by `play_type_slug` (not `play_instance_ref` — unlike the suggested-invite
 * card, a warm intro isn't tied to one campaign instance).
 *
 * The `warm_intro` play type row doesn't exist until U7 — until then
 * `matchedPlay` is always null and this degrades to the SAME
 * add-without-play fallback `useAddToQueueFromSuggestion` uses when its
 * campaign has no live match.
 *
 * Fix 2 (MAJOR bd finding): ALWAYS posts the add request, even when the
 * contact is already queued — `addQueueItem` is idempotent for a queued
 * contact (dedup-then-CAS, see queue.ts), and also fill-if-null CASes
 * `warmIntroId` onto the existing row when it's still unset. Never skip the
 * POST here — an early `if (queued) return` would silently discard the
 * CLICKED pill's `warmIntroId` when a different connector's pill is clicked
 * on an already-queued contact.
 */
export function useWarmIntroAdd(contactId: string, warmIntroId: string) {
  const { plays, groups, addContact, openQueueTab, playsResolved } = useEnrollQueue();
  const [pending, setPending] = useState(false);

  const queued = groups.some(g => g.items.some(it => it.contact_id === contactId));
  const matchedPlay = plays.find(p => p.play_type_slug === 'warm_intro') ?? null;
  const catalogLoading = !queued && !playsResolved;

  const onClick = useCallback(async () => {
    if (pending || catalogLoading) return;
    setPending(true);
    try {
      await addContact(contactId, {
        sourceContext: 'warm-intro',
        warmIntroId,
        ...(matchedPlay ? { playTypeId: matchedPlay.play_type_id, playInstanceRef: matchedPlay.play_instance_ref } : {}),
      });
      // Round-2 QA fix: open on the Plays tab (where the item just landed),
      // not open() — open() leaves whatever tab was already active, which
      // defaults to 'todos'.
      openQueueTab();
    } finally {
      setPending(false);
    }
  }, [pending, catalogLoading, addContact, contactId, warmIntroId, matchedPlay, openQueueTab]);

  const title = catalogLoading
    ? 'Loading play catalog…'
    : queued
      ? 'Already in the queue — open the queue'
      : matchedPlay
        ? `Add to queue for ${matchedPlay.name}`
        : 'Add to queue — no live warm intro play yet; assign manually';

  return { queued, pending, catalogLoading, title, onClick };
}

/**
 * The "View" action for a `launcher:campaign:<id>` fact (U11, plan
 * 2026-09-24-005 Item G). Opens `CampaignMemberSidebar` IN-PAGE via the
 * module-scope `useCampaignMemberSidebarStore` (U11a) instead of navigating
 * away. `PILL_CLICKED` fires here with `link_kind: 'intent'` (this is an
 * in-app action, not an outbound link, but it's the same rep-facing "pill
 * click" signal the retired anchor's onClick used to emit for this same
 * scheme) — exactly once per click, so a caller wiring both this hook and
 * its own tracking would double-fire; there is deliberately no second
 * `PILL_CLICKED` call site for this intent anywhere else.
 */
export function useCampaignMemberOpen({
  campaignId,
  contactId,
  observedAt,
  dimension,
  evidenceRef,
  surface,
}: {
  campaignId: string;
  contactId: string;
  observedAt: string;
  dimension: StateDimension;
  evidenceRef: string;
  surface: Surface;
}) {
  const open = useCampaignMemberSidebarStore((s) => s.open);
  const track = useSegmentTrack();

  const onClick = useCallback(() => {
    track(ANALYTICS_EVENTS.PILL_CLICKED, {
      dimension,
      link_kind: 'intent',
      evidence_ref: evidenceRef,
      contact_id: contactId,
      account_id: null,
      surface,
    });
    open({ campaignId, contactId, observedAt });
  }, [track, dimension, evidenceRef, contactId, surface, open, campaignId, observedAt]);

  return { onClick };
}
